import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import { TipService } from '@interacta-shared/feature-tip';
import { lastLoading } from '@interacta-shared/util';
import {
    AttachmentCategoryType,
    IAttachment,
    IListAttachments,
    isMediaAttachment,
} from '@interacta/util-post';
import {
    getMessageAttachmentUnavailableMessage,
    getReferenceUnavailableMessage,
} from '@modules/ai-shared/helpers/ai-shared.utils';
import {
    AISharedGenericReference,
    AISharedMessageAttachment,
    AISharedReferenceAvailability,
    AISharedReferenceType,
} from '@modules/ai-shared/models/ai-shared.model';
import { PostDetailSection } from '@modules/core/models/utility.model';
import { GalleryStateService } from '@modules/state/services/gallery-state.service';

/**
 * Opens an AI source, wherever it was clicked from: the card list under the
 * answer or an in-text reference chip.
 */
@Injectable({ providedIn: 'root' })
export class AISharedReferenceViewService {
    private readonly router = inject(Router);
    private readonly galleryStateService = inject(GalleryStateService);
    private readonly tipService = inject(TipService);
    private readonly gaService = inject(GoogleAnalyticsService);

    open(reference: AISharedGenericReference, origin: 'inline' | 'list'): void {
        // A source the BE marked unviewable carries identity fields only: opening
        // it would land on a post detail the user has no right to read.
        if (this.warnIfUnavailable(reference, getReferenceUnavailableMessage))
            return;

        this.gaService.sendCustomEvent('isp_ai_source_opened', {
            source: origin,
            type:
                reference.type === AISharedReferenceType.POST
                    ? 'post'
                    : reference.type === AISharedReferenceType.COMMENT
                      ? 'comment'
                      : 'attachment',
        });

        if (reference.type === AISharedReferenceType.POST) {
            this.router.navigate(['/post', reference.post.id]);
            return;
        }

        if (reference.type === AISharedReferenceType.COMMENT) {
            this.router.navigate(['/post', reference.comment.post.id], {
                queryParams: {
                    entity: PostDetailSection.COMMENTS,
                    commentId: reference.comment.id,
                },
            });
            return;
        }

        this.openAttachment(reference.attachment.attachmentData);
    }

    /** Opens the attachment a question was asked about, blocked the same way as a source. */
    openMessageAttachment(attachment: AISharedMessageAttachment): void {
        if (
            this.warnIfUnavailable(
                attachment,
                getMessageAttachmentUnavailableMessage,
            )
        )
            return;

        this.openAttachment(attachment.attachmentData);
    }

    private warnIfUnavailable(
        availability: AISharedReferenceAvailability,
        toMessageKey: (
            availability: AISharedReferenceAvailability,
        ) => string | null,
    ): boolean {
        const unavailableMessage = toMessageKey(availability);
        if (unavailableMessage) {
            this.tipService.warn(unavailableMessage);
            return true;
        }
        return false;
    }

    openAttachment(attachment: IAttachment): void {
        const attachmentList: IListAttachments = {
            attachments: [attachment],
            pageTokenInfo: lastLoading(),
            totalCount: 1,
            isLoading: false,
            isError: false,
            attachmentCategoryType: isMediaAttachment(attachment)
                ? AttachmentCategoryType.MULTIMEDIA
                : AttachmentCategoryType.DOCUMENT,
        };
        this.galleryStateService.openGallery(
            { feature: 'embedded', attachmentList },
            0,
            undefined,
            undefined,
            true,
        );
    }
}
